import { Logger as CommonLogger } from '@saviynt/common';

/**
 * The customLogger object serves as a custom wrapper around the Logger from @saviynt/common, 
 * replicating its logging methods (info, warn, error, etc.) while allowing the flexibility to 
 * introduce additional custom logic. By dynamically mapping all methods from Logger, 
 * customLogger enables you to call logging functions in the same way (customLogger.info(...)), 
 * making it easy to extend or modify logging behavior without changing the core API.

 * Key Benefits:
 * Event Validation: Allows you to implement specific logic to validate or modify events before logging, ensuring 
 *              that events are correctly processed according to your application's needs.
 * Chaining: You can create logs like Logger.info().error() and so on
 * Consistency: Maintains the same API as the original Logger, allowing you to use customLogger seamlessly 
 *              without modifying existing log calls.
 * Extensibility: Acts as a centralized point to introduce custom logic for logging, such as additional checks 
 *              or processing before calling the Logger method.
 * Dynamic Updates: Automatically maps methods from Logger, ensuring that any updates or new methods are 
 *              reflected in customLogger without manual intervention.
 */

// Pass this event to common logger. We can write specific logic there if log event is coming from ext
const event = 'rules-event';

/**
 * Infer all methods exposed by Logger as valid LoggerMethod types
 * keyof typeof Logger gets all the keys (including methods and properties).
 * `typeof Logger[K] extends (...args: any[]) => any ? K : never` checks if a key is a function.
 *       If true, it includes the key in the type; otherwise, it excludes it.
 */
type LoggerMethods = {
  [K in keyof typeof CommonLogger]: (typeof CommonLogger)[K] extends (
    ...args: unknown[]
  ) => unknown
    ? K
    : never;
}[keyof typeof CommonLogger];

// Define the structure of our custom logger
type LoggerType = {
  [K in LoggerMethods]: (...data: unknown[]) => LoggerType;
};

// Initialise an object to create your logger. It will be used by array reducer function
const LoggerInit = {} as LoggerType;

// Create an object that behaves like Logger
const Logger = Object.keys(CommonLogger).reduce<LoggerType>((acc, key) => {
  const fn = key as LoggerMethods;

  // Check if the Logger key is a function
  if (typeof CommonLogger[fn] === 'function') {
    // Create a proxy method on customLogger
    acc[fn] = (...data: unknown[]) => {
      CommonLogger[fn](event, ...data); // Call the original Logger method with multiple arguments

      return acc;
    };
  }

  return acc;
}, LoggerInit);

export default Logger;
